import {
  BadgePercent,
  Boxes,
  Building2,
  Contact,
  HandCoins,
  LayoutDashboard,
  MessageSquare,
  Package,
  Receipt,
  Shield,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'

import type { ReactElement } from 'react'

import { cn } from '@/lib/cn'
import { hasAnyPermission } from '@/lib/permissions'
import { useAuth } from '@/providers/auth-context'

export interface SidebarItem {
  to: string
  label: string
  icon: LucideIcon
  /** Permissões necessárias (any-of). Array vazio = público pros logados. */
  requiresAny: readonly string[]
}

export const SIDEBAR_ITEMS: readonly SidebarItem[] = [
  { to: '/app', label: 'Painel', icon: LayoutDashboard, requiresAny: ['view:dashboard'] },
  { to: '/app/customers', label: 'Clientes', icon: Contact, requiresAny: ['billing:customers'] },
  { to: '/app/companies', label: 'Empresas', icon: Building2, requiresAny: ['billing:companies'] },
  { to: '/app/sellers', label: 'Vendedores', icon: BadgePercent, requiresAny: ['billing:sellers'] },
  { to: '/app/plans', label: 'Planos', icon: Package, requiresAny: ['billing:customers'] },
  { to: '/app/invoices', label: 'Financeiro', icon: Receipt, requiresAny: ['billing:invoices'] },
  {
    to: '/app/commissions',
    label: 'Comissões',
    icon: HandCoins,
    requiresAny: ['billing:commissions'],
  },
  {
    to: '/app/suggestions',
    label: 'Sugestões',
    icon: MessageSquare,
    requiresAny: ['support:suggestions'],
  },
  { to: '/app/admin/users', label: 'Usuarios', icon: Users, requiresAny: ['admin:users'] },
  { to: '/app/admin/roles', label: 'Cargos', icon: Shield, requiresAny: ['admin:roles'] },
  // Sem permissão: o próprio perfil é de todo usuário logado.
  { to: '/app/perfil', label: 'Perfil', icon: UserCog, requiresAny: [] },
]

// Uma definição só para os três shells (staff, time e cliente): antes cada um repetia a própria
// string de classes e eles iam divergindo a cada retoque visual.
export function sidebarNavItemClass(isActive: boolean): string {
  return cn(
    'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
    // Barra de acento à esquerda: dá o "você está aqui" sem depender só do fundo.
    'before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-sidebar-primary before:opacity-0 before:transition-opacity',
    isActive
      ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-sm before:opacity-100'
      : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground',
  )
}

export const SIDEBAR_ASIDE_CLASS =
  'sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-sidebar-border bg-gradient-to-b from-sidebar to-sidebar/95 text-sidebar-foreground md:flex'

export const SIDEBAR_FOOTER_CLASS =
  'border-t border-sidebar-border/70 px-6 py-4 text-xs text-sidebar-foreground/50'

export const APP_HEADER_CLASS =
  'sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-border/70 bg-background/90 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70 sm:px-6'

export function SidebarBrand(): ReactElement {
  return (
    <div className="flex items-center gap-2.5 px-6 py-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sidebar-primary to-sidebar-primary/70 text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/20 ring-1 ring-inset ring-white/10">
        <Boxes className="h-5 w-5" aria-hidden />
      </span>
      <span className="text-lg font-semibold tracking-tight">Vanta Web</span>
    </div>
  )
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const { user } = useAuth()
  const userPerms = user?.permissions ?? []
  const items = SIDEBAR_ITEMS.filter((item) => hasAnyPermission(userPerms, item.requiresAny))

  return (
    <nav className="flex-1 space-y-1 px-3">
      {items.map((item) => {
        const Icon = item.icon
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end
            onClick={onNavigate}
            className={({ isActive }) => sidebarNavItemClass(isActive)}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {item.label}
          </NavLink>
        )
      })}
    </nav>
  )
}

export function Sidebar(): ReactElement {
  return (
    <aside className={SIDEBAR_ASIDE_CLASS}>
      <SidebarBrand />
      <SidebarNav />
      <div className={SIDEBAR_FOOTER_CLASS}>Vanta Web DDD</div>
    </aside>
  )
}
